import { ColdStartError, endpoints } from "@/api";
import type {
  CalculateEngagementRequest,
  CalculateEngagementResponse,
} from "@/types";

import type { EngagementService } from "../interfaces";

/**
 * REST-backed engagement service — Phase 6a.8b.
 *
 * Posts the full engagement payload to `POST /api/engagements/calculate`
 * (Backend Spec §20.4) and returns the typed response envelope.
 *
 * Cold-start retry: mirrors `RestCalculationService` behaviour for legacy
 * Render deployments. Railway has no cold start so retries are mostly
 * defensive.
 *
 * ── MANDATORY: Backend Finding Q workaround ────────────────────────────
 *
 * The backend currently does NOT propagate `engagement.functionalCurrency`
 * to per-contract calculations. Without this safeguard, foreign-currency
 * contracts whose payload omits `functionalCurrency` are calculated as
 * single-currency leases — silently suppressing FX revaluation entirely
 * (i.e., no PAJEs emitted, monetary remeasurement skipped).
 *
 * Until Backend Phase 5.7 implements server-side injection, this service
 * defensively copies `engagement.functionalCurrency` onto every contract
 * that lacks one before sending the request. Existing per-contract
 * `functionalCurrency` values are preserved unchanged (callers can still
 * set per-contract overrides for unusual cases).
 */
export class RestEngagementService implements EngagementService {
  async calculate(
    payload: CalculateEngagementRequest,
    options?: { signal?: AbortSignal },
  ): Promise<CalculateEngagementResponse> {
    const enriched = injectFunctionalCurrency(payload);

    try {
      return await endpoints.engagementCalculate(enriched, options);
    } catch (err) {
      // Honor abort: do not retry a cancelled request (Phase 6a.11).
      if (options?.signal?.aborted) throw err;
      if (err instanceof ColdStartError) {
        await new Promise((resolve) => setTimeout(resolve, 5000));
        if (options?.signal?.aborted) throw err;
        return await endpoints.engagementCalculate(enriched, options);
      }
      throw err;
    }
  }
}

/**
 * Backend Finding Q workaround. Exported for unit tests.
 *
 * For each contract that does not declare `functionalCurrency`, set it to
 * `engagement.functionalCurrency`. The payload is deep-copied so callers
 * see no mutation of their input.
 */
export function injectFunctionalCurrency(
  payload: CalculateEngagementRequest,
): CalculateEngagementRequest {
  const fc = payload.engagement.functionalCurrency;
  return {
    engagement: payload.engagement,
    contracts: payload.contracts.map((c) =>
      c.functionalCurrency === undefined
        ? { ...c, functionalCurrency: fc }
        : c,
    ),
  };
}
